/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SUPERADMIN_API_BASE } from '../config/api.config';

const SA_API = SUPERADMIN_API_BASE;

const SuperAdminContext = createContext(null);

export const SuperAdminProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [superAdmin, setSuperAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [otpChallenge, setOtpChallenge] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${SA_API}/auth.php?action=check_session`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (data.success && data.authenticated) {
        setSuperAdmin(data.user);
        setIsAuthenticated(true);
        setOtpChallenge(null);
      } else {
        setSuperAdmin(null);
        setIsAuthenticated(false);
        setOtpChallenge(data.otp_pending ? {
          maskedEmail: data.masked_email || 'your registered email',
          expiresIn: data.expires_in || 0,
        } : null);
      }
      return data;
    } catch (error) {
      console.error('Session check failed:', error);
      setSuperAdmin(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Super-admin authentication is only relevant to super-admin routes.
  useEffect(() => {
    if (!location.pathname.startsWith('/superadmin')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    checkSession();
  }, [checkSession, location.pathname]);

  const login = async (username, password, remember = false) => {
    try {
      // Use email field for login (can accept username too)
      const response = await fetch(`${SA_API}/auth.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'login',
          email: username, // API expects 'email' but accepts username format too
          password: password,
          remember: remember
        })
      });

      const data = await response.json();
      
      if (data.success && data.requires_otp) {
        setSuperAdmin(null);
        setIsAuthenticated(false);
        setOtpChallenge({
          maskedEmail: data.masked_email || 'your registered email',
          expiresIn: data.expires_in || 0,
        });
        return {
          success: true,
          requires_otp: true,
          masked_email: data.masked_email,
          message: data.message,
        };
      }

      if (data.success) {
        setSuperAdmin(data.user);
        setIsAuthenticated(true);
        setOtpChallenge(null);
        // Return redirect path from API
        return { 
          success: true, 
          user: data.user,
          redirect_path: data.redirect_path || '/superadmin/dashboard'
        };
      } else {
        return { success: false, message: data.message || data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${SA_API}/auth.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'logout'
        })
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setSuperAdmin(null);
      setIsAuthenticated(false);
      setOtpChallenge(null);
      navigate('/login');
    }
  };

  const value = {
    superAdmin,
    isAuthenticated,
    loading,
    login,
    logout,
    checkSession,
    otpChallenge,
    clearOtpChallenge: () => setOtpChallenge(null),
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export function useSuperAdmin() {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within a SuperAdminProvider');
  }
  return context;
}
